import { useState, useEffect, useMemo } from 'react'
import { X, Search, Loader2, Merge, Check } from 'lucide-react'
import { listFeedback, mergeFeedback, type FeedbackItem } from '../../lib/feedbackApi'

interface AdminMergeDialogProps {
  open: boolean
  targetItem?: FeedbackItem | null
  onClose: () => void
  onMerged: (mergedItem: FeedbackItem, sourceIds: number[]) => void
}

export function AdminMergeDialog({ open, targetItem, onClose, onMerged }: AdminMergeDialogProps) {
  const [allItems, setAllItems] = useState<FeedbackItem[]>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [selectedTarget, setSelectedTarget] = useState<number | null>(null)
  const [selectedSources, setSelectedSources] = useState<Set<number>>(new Set())
  const [merging, setMerging] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  // When dialog opens, fetch all items and set target
  useEffect(() => {
    if (!open) {
      // Reset state when closing
      setSearch('')
      setSelectedSources(new Set())
      setError(null)
      setSuccess(false)
      setMerging(false)
      return
    }

    setLoading(true)
    listFeedback({ per_page: 100 })
      .then((res) => {
        setAllItems(res.items)
        if (targetItem) {
          setSelectedTarget(targetItem.id)
        }
      })
      .catch(() => setError('Failed to load feedback items'))
      .finally(() => setLoading(false))
  }, [open, targetItem])

  // Filter items by search
  const filteredItems = useMemo(() => {
    if (!search.trim()) return allItems
    const q = search.toLowerCase()
    return allItems.filter(
      (item) =>
        item.title.toLowerCase().includes(q) ||
        (item.description && item.description.toLowerCase().includes(q))
    )
  }, [allItems, search])

  // Items available as sources (exclude the target)
  const sourceItems = useMemo(
    () => filteredItems.filter((item) => item.id !== selectedTarget),
    [filteredItems, selectedTarget]
  )

  function toggleSource(id: number) {
    setSelectedSources((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  async function handleMerge() {
    if (!selectedTarget || selectedSources.size === 0 || merging) return

    setMerging(true)
    setError(null)
    try {
      const sourceIds = Array.from(selectedSources)
      const mergedItem = await mergeFeedback(selectedTarget, sourceIds)
      setSuccess(true)
      setTimeout(() => {
        onMerged(mergedItem, sourceIds)
        onClose()
      }, 1000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Merge failed')
    } finally {
      setMerging(false)
    }
  }

  if (!open) return null

  const targetTitle = allItems.find((i) => i.id === selectedTarget)?.title || 'Select a target'

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 z-50 bg-black/30" onClick={onClose} />

      {/* Dialog */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border shrink-0">
            <div className="flex items-center gap-2">
              <Merge className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-semibold">Merge Feedback</h2>
            </div>
            <button onClick={onClose} className="p-1 rounded-lg hover:bg-secondary transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : success ? (
              <div className="flex flex-col items-center justify-center py-8 gap-2">
                <div className="w-10 h-10 bg-green-500/10 rounded-full flex items-center justify-center">
                  <Check className="w-5 h-5 text-green-600" />
                </div>
                <p className="text-sm font-medium text-green-600">Items merged successfully</p>
              </div>
            ) : (
              <>
                {/* Target selection */}
                <div>
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide block mb-1.5">
                    Keep (target)
                  </label>
                  {targetItem ? (
                    <div className="p-2.5 bg-primary/5 border border-primary/20 rounded-lg text-sm font-medium">
                      {targetTitle}
                    </div>
                  ) : (
                    <select
                      value={selectedTarget ?? ''}
                      onChange={(e) => {
                        const val = e.target.value ? Number(e.target.value) : null
                        setSelectedTarget(val)
                        // Remove from sources if was selected
                        if (val) {
                          setSelectedSources((prev) => {
                            const next = new Set(prev)
                            next.delete(val)
                            return next
                          })
                        }
                      }}
                      className="w-full p-2.5 bg-card border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="">Select target item...</option>
                      {allItems.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.title}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Source items to merge */}
                <div>
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide block mb-1.5">
                    Merge into target ({selectedSources.size} selected)
                  </label>

                  {/* Search */}
                  <div className="relative mb-2">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search feedback items..."
                      className="w-full pl-8 pr-3 py-2 bg-card border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground"
                    />
                  </div>

                  {/* Item list with checkboxes */}
                  <div className="border border-border rounded-lg max-h-48 overflow-y-auto divide-y divide-border">
                    {sourceItems.length === 0 ? (
                      <p className="p-3 text-xs text-muted-foreground text-center">
                        {selectedTarget ? 'No other items to merge' : 'Select a target first'}
                      </p>
                    ) : (
                      sourceItems.map((item) => (
                        <label
                          key={item.id}
                          className="flex items-center gap-3 p-2.5 hover:bg-secondary/50 cursor-pointer transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={selectedSources.has(item.id)}
                            onChange={() => toggleSource(item.id)}
                            className="rounded border-border"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{item.title}</p>
                            <p className="text-[10px] text-muted-foreground">
                              {item.vote_count} votes &middot; {item.comment_count} comments
                            </p>
                          </div>
                        </label>
                      ))
                    )}
                  </div>
                </div>

                {/* Preview */}
                {selectedTarget && selectedSources.size > 0 && (
                  <div className="p-3 bg-amber-500/5 border border-amber-500/20 rounded-lg text-xs text-amber-600 dark:text-amber-400">
                    Merging {selectedSources.size} item{selectedSources.size > 1 ? 's' : ''} into &ldquo;{targetTitle}&rdquo; &mdash; votes and comments will be combined.
                  </div>
                )}

                {error && (
                  <div className="p-3 text-sm bg-red-500/10 text-red-600 dark:text-red-400 rounded-lg">
                    {error}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          {!success && !loading && (
            <div className="flex items-center justify-end gap-2 p-4 border-t border-border shrink-0">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm rounded-lg hover:bg-secondary transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleMerge}
                disabled={!selectedTarget || selectedSources.size === 0 || merging}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {merging && <Loader2 className="w-4 h-4 animate-spin" />}
                Merge {selectedSources.size > 0 ? `(${selectedSources.size})` : ''}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
